import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchCollection, getLocalized } from '../lib/cms'
import ContactForm from '../components/ContactForm'

import FAQSection from '../components/FAQSection'
import { updateMeta } from '../lib/utils'
import { 
  Calculator, 
  Percent, 
  FileText, 
  FileCheck, 
  TrendingUp, 
  Users, 
  Briefcase, 
  Check, 
  ArrowRight,
  HelpCircle
} from 'lucide-react'

const iconMap = {
  Calculator,
  Percent,
  FileText,
  FileCheck,
  TrendingUp,
  Users,
  Briefcase
}

export default function ServicesPage() {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedService, setSelectedService] = useState('')

  useEffect(() => {
    updateMeta(
      "Ydelser | Thellefsen Regnskab",
      "Oversigt over vores ydelser inden for årsregnskab, bogføring og moms. Skræddersyede løsninger til enkeltmandsvirksomheder og mindre selskaber."
    )
    async function loadData() {
      try {
        const servicesData = await fetchCollection('services')
        setServices(servicesData.filter(s => {
          const sSlug = s.slug?.da || s.slug || ''
          return sSlug !== 'personlig-selvangivelse'
        }))
      } catch (error) {
        console.error('Error loading services page data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const handleSelectServiceForForm = (serviceSlug) => {
    setSelectedService(serviceSlug)
    const formElement = document.getElementById('kontakt-form-section')
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      {/* Header Section */}
      <section className="bg-white border-b border-stone-200 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 tracking-tight">
            Vores Ydelser
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Professionel, personlig og gennemskuelig revisorhjælp hos{' '}
            <Link to="/" className="text-stone-900 font-semibold hover:text-orange-600 transition-colors underline decoration-stone-300 underline-offset-2">
              Thellefsen Regnskab
            </Link>
            , tilpasset dine og din virksomheds behov.
          </p>
        </div>
      </section>

      {/* Featured: Kerneydelser & Guides callouts */}
      <section className="bg-stone-100/60 border-b border-stone-200 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          {/* Hovedbanner for samlet regnskabsservice */}
          <div className="bg-white border border-stone-200 p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm mb-6">
            <div className="space-y-1 max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
                Samlet regnskabspakke
              </span>
              <h2 className="text-xl sm:text-2xl font-bold text-stone-900">
                Få hjælp til regnskab og mere tid til din virksomhed
              </h2>
              <p className="text-sm text-stone-600 leading-relaxed">
                Vil du bruge mindre tid på bilag, moms og regnskab? Saml den løbende bogføring, moms og årets regnskab hos Peter til en fast årlig pris.
              </p>
            </div>
            <Link
              to="/regnskabsservice"
              className="inline-flex items-center justify-center shrink-0 rounded-none bg-orange-600 px-5 py-3 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
            >
              Læs om regnskabsservice
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          {/* To fremhævede sektioner for Bogføring og Årsrapport */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-stone-200 p-6 flex flex-col justify-between shadow-sm hover:border-orange-500/40 transition-colors">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
                  Løbende bogføring & moms
                </span>
                <h3 className="text-lg font-bold text-stone-900 mt-1">
                  Bogføring, der giver dig mere tid
                </h3>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  Få håndteret bilag, bank og moms løbende, så du har ro i maven og et solidt beslutningsgrundlag.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                <Link
                  to="/bogfoering"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4"
                >
                  Læs mere om bogføring
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="bg-white border border-stone-200 p-6 flex flex-col justify-between shadow-sm hover:border-orange-500/40 transition-colors">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
                  Årsafslutning & selskabsrapportering
                </span>
                <h3 className="text-lg font-bold text-stone-900 mt-1">
                  Årsrapport og årsregnskab med styr på tallene
                </h3>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  Få udarbejdet årsrapport og årsregnskab efter gældende regler for ApS og enkeltmandsvirksomheder.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                <Link
                  to="/aarsrapport-og-aarsregnskab"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4"
                >
                  Læs mere om årsrapport
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>



      {/* Detailed Services Grid */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="space-y-12">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-64 rounded-none bg-stone-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-16">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon_name] || Calculator
                const isEven = index % 2 === 0
                const sSlug = service.slug?.da || service.slug || ''
                let detailLink = `/ydelser/${sSlug}`
                if (sSlug === 'aarsregnskab-og-bogfoering') detailLink = '/regnskabsservice'
                if (sSlug === 'aarsregnskab') detailLink = '/aarsrapport-og-aarsregnskab'

                return (
                  <div 
                    key={service.id}
                    id={sSlug}
                    className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center rounded-none border border-stone-200 bg-white p-8 sm:p-12 shadow-sm scroll-mt-24 transition-all duration-300 hover:shadow-md`}
                  >
                    {/* Icon & Title Column */}
                    <div className={`lg:col-span-5 space-y-6 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                      <div className="inline-flex h-14 w-14 items-center justify-center rounded-none bg-orange-50 text-orange-600">
                        <IconComponent className="h-7 w-7" />
                      </div>
                      <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
                        {getLocalized(service.title)}
                      </h2>
                      <p className="text-stone-600 leading-relaxed">
                        {getLocalized(service.description)}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <Link
                          to={detailLink}
                          className="inline-flex items-center justify-center gap-2 rounded-none bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer"
                        >
                          Læs mere om ydelsen
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleSelectServiceForForm(sSlug)}
                          className="inline-flex items-center justify-center gap-2 rounded-none bg-stone-100 border border-stone-300 px-5 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer"
                        >
                          Få et tilbud
                        </button>
                      </div>
                    </div>

                    {/* Details Column */}
                    <div className={`lg:col-span-7 bg-stone-50 rounded-none p-6 sm:p-8 border border-stone-200/60 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                      <h3 className="text-lg font-bold text-stone-900 mb-4">Hvad inkluderer denne ydelse typisk?</h3>
                      <ul className="space-y-3 text-sm text-stone-700">
                        <li className="flex items-start gap-2.5">
                          <Check className="h-4.5 w-4.5 text-orange-600 shrink-0 mt-0.5" />
                          <span><strong>Skræddersyet rådgivning:</strong> Vi tager udgangspunkt i din specifikke situation og branche.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                          <Check className="h-4.5 w-4.5 text-orange-600 shrink-0 mt-0.5" />
                          <span><strong>Fuld overholdelse af lovgivningen:</strong> We sikrer, at alle regler, frister og indberetninger overholdes til punkt og prikke.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                          <Check className="h-4.5 w-4.5 text-orange-600 shrink-0 mt-0.5" />
                          <span><strong>Direkte sparring:</strong> Du kan altid ringe eller skrive direkte til Peter, hvis der opstår spørgsmål.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                          <Check className="h-4.5 w-4.5 text-orange-600 shrink-0 mt-0.5" />
                          <span><strong>Fast, aftalt pris:</strong> Ingen ubehagelige overraskelser eller uforudsete ekstraregninger.</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="Ofte stillede spørgsmål om vores ydelser"
        subtitle="Her kan du læse mere om vores faste priser, arbejdsgange og hvordan vi hjælper din virksomhed."
        items={[
          {
            question: "Hvordan kommer jeg hurtigt i gang med Thellefsen Regnskab?",
            answer: (
              <>
                Det er enkelt og ligetil at komme i gang. Vi starter med en uforpligtende samtale over telefonen eller via e-mail, hvor vi afklarer dine konkrete behov og gennemgår din nuværende opsætning. Herefter modtager du et fast skriftligt tilbud, og vi hjælper dig trygt igennem eventuel overdragelse af materiale fra din tidligere bogholder eller revisor.
              </>
            )
          },
          {
            question: "Hvad koster en revisor om året, og er priserne faste?",
            answer: (
              <>
                Hos Thellefsen Regnskab arbejder vi udelukkende med gennemskuelige og faste priser, så du undgår ubehagelige regninger baseret på dyre timetakster. Vores årsregnskab starter fra 4.000 kr. ekskl. moms for anpartsselskaber (ApS) og enkeltmandsvirksomheder, mens vores komplette regnskabspakker med løbende bogføring og momsindberetning afregnes til faste, forudsigelige månedlige eller årlige beløb.
              </>
            )
          },
          {
            question: "Er regnskabsprogram og softwarelicens inkluderet i jeres priser?",
            answer: (
              <>
                Ja, vælger du os til at stå for bogføringen, så er der regnskabsprogram inkluderet i prisen.
              </>
            )
          },
          {
            question: "Hvem taler jeg med, når jeg har spørgsmål til mit regnskab?",
            answer: (
              <>
                Du har altid direkte personlig kontakt til den samme person, hver gang du ringer til Thellefsen Regnskab.
              </>
            )
          }
        ]}
      />

      {/* Contact Form Section */}
      <section className="py-20 bg-stone-100 border-t border-stone-200" id="kontakt-form-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <ContactForm 
            defaultService={selectedService} 
            title="Få et uforpligtende tilbud"
            subtitle="Udfyld formularen, så vender vi tilbage inden for 24 timer."
          />
        </div>
      </section>
    </div>
  )
}
